import { Component, forwardRef, HostBinding, Input } from '@angular/core';
import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';
import { ModeView } from '../../../shared/ontology/model/mode-view.enum';
import { isEditableByMode } from '../utils/ontology-editing.util';

@Component({
    selector: 'jhi-input',
    template: `
		@if (isEditable()) {
			<input
				type="text"
				class="form-control"
				[disabled]="disabled"
				[required]="required"
				[value]="value ?? ''"
				(input)="onInternalChange($event)"
				(blur)="markTouched()"
			/>
		} @else {
			<div class="form-control-plaintext">
				<truncate-span [textInput]="value || defaultValue"></truncate-span>
			</div>
		}
    `,
    styles: [
        `
            :host.ng-valid.ng-touched input.form-control {
                border-left: 6px solid var(--bs-success);
                padding-left: calc(.75rem - 6px);
            }

            /* In CREATE modeView, show invalid immediately (even before touch). */
            :host.modeView-create.ng-invalid input.form-control {
                border-left: 6px solid var(--bs-danger);
                padding-left: calc(.75rem - 6px);
            }

            /* In other modes, show invalid only after touch. */
            :host.ng-invalid.ng-touched input.form-control {
                border-left: 6px solid var(--bs-danger);
                padding-left: calc(.75rem - 6px);
            }
        `
    ],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => InputComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => InputComponent),
            multi: true
        }
    ]
})
export class InputComponent implements ControlValueAccessor, Validator {
    @Input() modeView: ModeView = ModeView.VIEW;
    @Input() model?: any;
    @Input() property?: string;
    @Input() disabled = false;
    @Input() defaultValue: string | null = '';

    private _required = false;
    private _maxLength: number | null = null;
    private _pattern: string | RegExp | null = null;

    value: string | null = null;

    private onChange: (v: string | null) => void = () => {
    };
    private onTouchedCallback: () => void = () => {
    };
    private validatorChangeFn: () => void = () => {
    };

    @Input()
    set required(v: boolean) {
        this._required = v === true || String(v) === 'true';
        this.validatorChangeFn();
    }

    get required(): boolean {
        return this._required;
    }

    @Input()
    set maxLength(v: number | null) {
        this._maxLength = v;
        this.validatorChangeFn();
    }

    get maxLength(): number | null {
        return this._maxLength;
    }

    @Input()
    set pattern(v: string | RegExp | null) {
        this._pattern = v;
        this.validatorChangeFn();
    }

    get pattern(): string | RegExp | null {
        return this._pattern;
    }

    @HostBinding('class.modeView-create')
    public get isCreateMode(): boolean {
        return this.modeView === ModeView.CREATE;
    }

    writeValue(v: string | null): void {
        this.value = v ?? null;
        this.validatorChangeFn();
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouchedCallback = fn;
    }

    registerOnValidatorChange(fn: () => void): void {
        this.validatorChangeFn = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    public onInternalChange(event: Event): void {
        const target = event.target as HTMLInputElement | null;
        if (!target) {
            return;
        }

        this.value = target.value ?? '';
        this.onChange(this.value);
        this.validatorChangeFn();
    }

    public markTouched(): void {
        this.onTouchedCallback();
    }

    public isEditable(): boolean {
        return isEditableByMode({
            modeView: this.modeView,
            model: this.model,
            property: this.property ?? ''
        });
    }

    public validate(control: AbstractControl): ValidationErrors | null {
        if (this.modeView === ModeView.VIEW) {
            return null;
        }

        const current = (control.value ?? '').toString();

        if (this.required && current.trim().length === 0) {
            return { required: true };
        }

        if (typeof this.maxLength === 'number' &&
            this.maxLength >= 0 &&
            current.length > this.maxLength
        ) {
            return { maxlength: true };
        }

        if (this.pattern) {
            const re = this.pattern instanceof RegExp ? this.pattern : new RegExp(this.pattern);
            if (current && !re.test(current)) {
                return { pattern: true };
            }
        }
        return null;
    }
}
